import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

import type { BulkProgressState } from '@/shared/actions/erp/tickets-soporte-cliente';

type DialogBulkProgressProps = {
  open: boolean;
  onClose: () => void;
  progress: BulkProgressState | null;
  isPolling: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function DialogBulkProgress({ open, onClose, progress, isPolling: _isPolling }: DialogBulkProgressProps) {
  const isFinished = progress?.estado === 'COMPLETADO' || progress?.estado === 'ERROR';
  const exitosos = progress ? progress.total - (progress.errores?.length || 0) : 0;
  const fallidos = progress?.errores?.length || 0;

  return (
    <Dialog
      open={open}
      onClose={isFinished ? onClose : undefined}
      disableEscapeKeyDown={!isFinished}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Operación Masiva en Progreso</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {progress?.descripcion || 'Preparando...'}
          </Typography>
          <LinearProgress
            variant={progress?.porcentaje ? 'determinate' : 'indeterminate'}
            value={progress?.porcentaje || 0}
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {progress?.progreso || 0} de {progress?.total || 0} ({progress?.porcentaje || 0}%)
          </Typography>
        </Box>

        {isFinished && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Chip
              icon={<CheckCircleIcon />}
              label={`Exitosos: ${exitosos}`}
              color="success"
              variant="outlined"
              size="small"
            />
            {fallidos > 0 && (
              <Chip
                icon={<ErrorIcon />}
                label={`Fallidos: ${fallidos}`}
                color="error"
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        )}

        {isFinished && fallidos > 0 && (
          <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
            <Typography variant="subtitle2" gutterBottom>
              Errores:
            </Typography>
            <List dense disablePadding>
              {progress?.errores?.map((err, idx) => (
                <ListItem key={idx} disableGutters sx={{ py: 0.25 }}>
                  <ListItemText
                    primary={
                      <Typography variant="caption" color="error">
                        Ticket {err.ticket_id.substring(0, 8)}...: {err.error}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant="contained"
          disabled={!isFinished}
        >
          {isFinished ? 'Cerrar' : 'Procesando...'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
